import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Clock, 
  User, 
  Calendar, 
  CheckCircle, 
  AlertTriangle, 
  Loader2,
  BookOpen,
  XCircle,
  FileText,
  Upload,
  GraduationCap
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AssignmentHoursEntryPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [assignmentInfo, setAssignmentInfo] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  
  // Form fields
  const [sessionDate, setSessionDate] = useState(new Date().toISOString().split('T')[0]);
  const [sessionTime, setSessionTime] = useState('');
  const [hoursUsed, setHoursUsed] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [lessonTopic, setLessonTopic] = useState('');
  const [lessonNotesFile, setLessonNotesFile] = useState(null);
  const [lessonNotesUrl, setLessonNotesUrl] = useState('');
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [notes, setNotes] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadAssignmentInfo();
  }, [token]);

  const loadAssignmentInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}/api/assignment/${token}`);
      setAssignmentInfo(response.data);
    } catch (err) {
      console.error('Error loading assignment info:', err);
      if (err.response?.status === 404 || err.response?.status === 403) {
        setError(err.response?.data?.detail || 'This submission link is no longer active for this student.');
      } else {
        setError('Failed to load assignment information. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Invalid file type. Only PDF files are allowed for lesson notes.');
      e.target.value = '';
      return;
    }
    
    // Validate file size (max 20 MB)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 20) {
      toast.error(`File too large (${fileSizeMB.toFixed(1)} MB). Maximum file size is 20 MB.`);
      e.target.value = '';
      return;
    }
    
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/upload/lesson-notes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setLessonNotesFile(file);
      setLessonNotesUrl(response.data.url);
      toast.success('Lesson notes uploaded successfully');
    } catch (err) {
      console.error('Error uploading PDF:', err);
      toast.error('Failed to upload lesson notes. Please try again.');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!sessionDate) {
      toast.error('Please enter the session date');
      return;
    }
    if (!sessionTime) {
      toast.error('Please enter the session time');
      return;
    }
    if (!hoursUsed || parseFloat(hoursUsed) <= 0) {
      toast.error('Please enter valid hours used');
      return;
    }
    if (!gradeLevel) {
      toast.error('Please select a grade level');
      return;
    }
    if (!lessonTopic.trim()) {
      toast.error('Please enter the lesson topic');
      return;
    }
    if (!lessonNotesUrl) {
      toast.error('Please upload lesson notes PDF before submitting');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/assignment/${token}/log-session`, {
        session_date: sessionDate,
        session_time: sessionTime,
        hours_used: parseFloat(hoursUsed),
        grade_level: gradeLevel,
        lesson_topic: lessonTopic.trim(),
        lesson_notes_url: lessonNotesUrl,
        notes: notes.trim()
      });
      
      setSubmitResult(response.data);
      setSubmitted(true);
      toast.success('Session hours logged successfully!');
    } catch (err) {
      console.error('Error submitting hours:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit hours. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mb-4" />
            <p className="text-slate-600">Loading assignment information...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <XCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Link Invalid</h2>
            <p className="text-slate-600 text-center">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-green-200">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">Hours Submitted!</h2>
            <p className="text-slate-600 text-center mb-4">
              You logged {submitResult?.hours_logged} hours for {submitResult?.student_name}
            </p>
            <div className="bg-slate-50 rounded-lg p-4 w-full text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Session Date:</span>
                <span className="font-medium">{submitResult?.session_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Grade Level:</span>
                <span className="font-medium capitalize">{submitResult?.grade_level?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Remaining Hours:</span>
                <span className="font-medium">{submitResult?.total_hours_remaining}</span>
              </div>
            </div>
            {submitResult?.warning && (
              <Alert className="mt-4 border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700">
                  {submitResult.warning}
                </AlertDescription>
              </Alert>
            )}
            <Button 
              className="mt-6"
              onClick={() => {
                setSubmitted(false);
                setSessionTime('');
                setHoursUsed('');
                setGradeLevel('');
                setLessonTopic('');
                setLessonNotesFile(null);
                setLessonNotesUrl('');
                setNotes('');
                loadAssignmentInfo();
              }}
            >
              Log Another Session
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-4">
            <Clock className="h-8 w-8 text-indigo-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Log Tutoring Session</h1>
          <p className="text-slate-600 mt-2">Submit your session hours for approval</p>
        </div>

        {/* Assignment Info Card */}
        <Card className="mb-6 border-indigo-100">
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-indigo-600" />
                <div>
                  <p className="text-xs text-slate-500">Student</p>
                  <p className="font-medium text-slate-900">{assignmentInfo?.student_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <GraduationCap className="h-5 w-5 text-indigo-600" />
                <div>
                  <p className="text-xs text-slate-500">Tutor</p>
                  <p className="font-medium text-slate-900">{assignmentInfo?.tutor_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 col-span-2">
                <Clock className="h-5 w-5 text-indigo-600" />
                <div>
                  <p className="text-xs text-slate-500">Hours Remaining</p>
                  <p className="font-medium text-slate-900">
                    {assignmentInfo?.total_hours_remaining} of {assignmentInfo?.total_hours_purchased} hours
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Remaining Hours Warning */}
        {assignmentInfo?.total_hours_remaining <= 0 && (
          <Alert className="mb-6 border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-700">
              This student has no remaining hours. Please contact the administrator.
            </AlertDescription>
          </Alert>
        )}

        {/* Session Form */}
        <Card>
          <CardHeader>
            <CardTitle>Session Details</CardTitle>
            <CardDescription>
              Enter the details of your tutoring session
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sessionDate">Session Date *</Label>
                  <Input
                    id="sessionDate"
                    type="date"
                    value={sessionDate}
                    onChange={(e) => setSessionDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    data-testid="session-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sessionTime">Session Time *</Label>
                  <Input
                    id="sessionTime"
                    type="time"
                    value={sessionTime}
                    onChange={(e) => setSessionTime(e.target.value)}
                    required
                    data-testid="session-time-input"
                  />
                </div>
              </div>

              {/* Hours and Grade Level */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hoursUsed">Hours Used *</Label>
                  <Input
                    id="hoursUsed"
                    type="number"
                    min="0.5"
                    max="8"
                    step="0.5"
                    value={hoursUsed}
                    onChange={(e) => setHoursUsed(e.target.value)}
                    placeholder="e.g., 1.5"
                    required
                    data-testid="hours-used-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gradeLevel">Grade Level *</Label>
                  <Select value={gradeLevel} onValueChange={setGradeLevel} required>
                    <SelectTrigger data-testid="grade-level-select">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high_school">High School</SelectItem>
                      <SelectItem value="university">University</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Lesson Topic */}
              <div className="space-y-2">
                <Label htmlFor="lessonTopic">Lesson Topic *</Label>
                <Input
                  id="lessonTopic"
                  value={lessonTopic}
                  onChange={(e) => setLessonTopic(e.target.value)}
                  placeholder="e.g., Quadratic Equations, Essay Writing"
                  required
                  data-testid="lesson-topic-input"
                />
              </div>

              {/* Lesson Notes Upload */}
              <div className="space-y-2">
                <Label>Lesson Notes (PDF) *</Label>
                <div 
                  className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handlePdfUpload}
                    className="hidden"
                    data-testid="lesson-notes-upload"
                  />
                  {uploadingPdf ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-2" />
                      <p className="text-sm text-slate-600">Uploading...</p>
                    </div>
                  ) : lessonNotesFile ? (
                    <div className="flex flex-col items-center">
                      <FileText className="h-8 w-8 text-green-600 mb-2" />
                      <p className="text-sm font-medium text-slate-900">{lessonNotesFile.name}</p>
                      <p className="text-xs text-green-600 mt-1">Uploaded successfully</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-8 w-8 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-600">Click to upload lesson notes PDF</p>
                      <p className="text-xs text-slate-400 mt-1">Maximum file size: 20 MB</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information about the session..."
                  rows={3}
                  data-testid="notes-input"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || !lessonNotesUrl}
                data-testid="submit-hours-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Submit Session Hours
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
